import { executeRequest } from '@/core/either';
import { http } from '@/core/api';
import type { ApiResult } from '@/core/either';
import type { KycCompliance } from '@/types/KycCompliance';
import type { RefreshTokenResponse } from '@/types/Login';
import { REFRESH_TOKEN_KEY } from '../constants/auth.constant';
import {
  API_VERSION_HEADER,
  API_VERSION_STRICT_BENEFICIARY,
} from '../constants/apiVersion.constant';

export const authService = {
  /**
   * Obtiene el estado del KYC de la empresa.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getKycStatus(): Promise<ApiResult<KycCompliance>> {
    return executeRequest(() => http.get<KycCompliance>('company/b2b/kyc/status'));
  },

  /**
   * Obtiene el estado del KYC resolviendo `hasBeneficiaryFile` contra los
   * beneficiarios finales aprobados, sin considerar el documento generado.
   * La versión por defecto del endpoint acepta cualquiera de los dos, por lo que
   * una empresa con documento y sin beneficiarios aprobados queda como cumplida.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getKycStatusStrictBeneficiary(): Promise<ApiResult<KycCompliance>> {
    return executeRequest(() =>
      http.get<KycCompliance>('company/b2b/kyc/status', {
        headers: { [API_VERSION_HEADER]: API_VERSION_STRICT_BENEFICIARY },
      })
    );
  },

  /**
   * Refresca el token de autenticación usando el refreshToken guardado desde el login.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  refreshToken(): Promise<ApiResult<RefreshTokenResponse>> {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY) || '';
    return executeRequest(() => {
      return http.post<RefreshTokenResponse>('auth-server/b2b/auth/refresh-token', {
        refreshToken,
      });
    });
  },

  /**
   * Cierra la sesión del usuario en el servidor.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  logout(): Promise<ApiResult<void>> {
    return executeRequest(() => http.post('auth-server/b2b/auth/logout'));
  },
};